import { useEffect, useMemo, useState } from "react";
import { api, formatINR, downloadXlsx } from "@/lib/api";
import { Loader2, Plus, X, Check, XCircle, MapPin, Car, Bike, Hotel, UtensilsCrossed, Download, FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  pending: { bg: "#FCE7C9", color: "#9A6020" },
  approved: { bg: "#D8F3DC", color: "#1B4332" },
  rejected: { bg: "#FADED7", color: "#9A3412" },
};

export default function Tada() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [department, setDepartment] = useState("");

  const load = async () => {
    setLoading(true);
    const params = { month };
    if (department) params.department = department;
    const { data } = await api.get("/tada", { params });
    setItems(data); setLoading(false);
  };

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/departments").then((r) => setDepartments(r.data || []));
  }, []);
  useEffect(() => { load(); }, [month, department]);

  const setStatus = async (id, status) => {
    await api.put(`/tada/${id}/status`, { status });
    load();
  };

  const canApprove = user?.role !== "employee";

  // Unique department list — canonical departments from API + any extra ones from employees
  const departmentOptions = useMemo(() => {
    const set = new Set(departments.map((d) => d.name).filter(Boolean));
    employees.forEach((e) => {
      const d = (e.department_name || e.department || "").trim();
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [employees, departments]);

  // Sort items by department -> employee name for the statement view
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const da = (a.employee_department || "").toLowerCase();
      const db = (b.employee_department || "").toLowerCase();
      if (da !== db) return da.localeCompare(db);
      return (a.employee_name || "").localeCompare(b.employee_name || "");
    });
  }, [items]);

  const grandTotal = useMemo(() => sortedItems.reduce((s, t) => s + (t.actual_expense || 0), 0), [sortedItems]);

  const monthLabel = useMemo(() => {
    if (!month) return "";
    const [y, mm] = month.split("-");
    const names = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
    return `${names[parseInt(mm, 10) - 1]} ${y}`;
  }, [month]);

  const exportClaimsCsv = () => {
    const headers = ["S.No", "Employee Name", "Designation", "Amount", "Location", "Dealer Names"];
    const rows = sortedItems.map((t, i) => [
      i + 1,
      `"${t.employee_name || ""}"`,
      `"${t.employee_designation || ""}"`,
      t.actual_expense || 0,
      `"${t.location || ""}"`,
      `"${(t.dealer_names || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [
      `TA DA STATEMENT FOR THE MONTH OF ${monthLabel}`,
      department ? `DEPARTMENT: ${department}` : "",
      "",
      headers.join(","),
      ...rows.map((r) => r.join(",")),
      `,,TOTAL,${grandTotal},,`,
    ].filter(Boolean).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `tada_statement_${month}${department ? "_" + department.replace(/\s+/g, "_") : ""}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="tada-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Field Expenses</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">TA / DA Statement</h1>
          <p className="text-stone-600 mt-1 text-sm">Monthly TA/DA statement sortable by department.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadXlsx("/tada/export.xlsx",
              `tada_statement_${month}${department ? "_" + department.replace(/\s+/g, "_") : ""}.xlsx`,
              { month, ...(department ? { department } : {}) })}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
            style={{ background: "#1B4332" }} data-testid="export-tada-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportClaimsCsv} className="flex items-center gap-2 px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="export-tada-csv">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => setShow(true)} data-testid="new-claim-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> New Claim
          </button>
        </div>
      </header>

      {/* tab removed — Dealer Receipts feature retired per request */}
      <>
        <div className="flex items-end gap-3 flex-wrap">
            <label className="flex flex-col gap-1">
              <span className="kpi-label">Month</span>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="tada-month"
                     className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[10rem]" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="kpi-label">Department</span>
              <select value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="tada-department-filter"
                      className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[14rem]">
                <option value="">All Departments</option>
                {departmentOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <div className="ml-auto text-sm">
              <span className="text-stone-500">Total entries ({sortedItems.length}): </span>
              <span className="font-heading text-lg font-bold" style={{ color: "var(--color-primary)" }}>{formatINR(grandTotal)}</span>
            </div>
          </div>

          {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
            <div className="card-flat p-0 overflow-hidden border border-stone-300">
              <div className="text-center font-heading font-bold text-sm py-3 border-b border-stone-300 uppercase tracking-wider">
                TA DA STATEMENT FOR THE MONTH OF {monthLabel}
                {department && <div className="mt-1 text-xs" style={{ color: "var(--color-secondary)" }}>Department: {department}</div>}
              </div>
              {sortedItems.length === 0 ? (
                <div className="py-14 text-center text-stone-500 text-sm" data-testid="tada-empty">No claims for {monthLabel}. Click &quot;New Claim&quot; to add.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid="tada-statement-table">
                    <thead>
                      <tr className="text-xs uppercase tracking-wider bg-stone-50 border-b-2 border-stone-300">
                        <th className="px-3 py-3 text-left border-r border-stone-200 w-12">S.No</th>
                        <th className="px-3 py-3 text-left border-r border-stone-200">Employee Name</th>
                        <th className="px-3 py-3 text-left border-r border-stone-200">Designation</th>
                        <th className="px-3 py-3 text-right border-r border-stone-200">Amount</th>
                        <th className="px-3 py-3 text-left border-r border-stone-200">Location</th>
                        <th className="px-3 py-3 text-left border-r border-stone-200">Dealer Names</th>
                        <th className="px-3 py-3 text-left">Status</th>
                        {canApprove && <th className="px-3 py-3"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((t, i) => {
                        const c = STATUS[t.status] || STATUS.pending;
                        return (
                          <tr key={t.id} className="border-t border-stone-200 hover:bg-stone-50/60">
                            <td className="px-3 py-2.5 border-r border-stone-100">{i + 1}</td>
                            <td className="px-3 py-2.5 border-r border-stone-100 font-medium uppercase">{t.employee_name}</td>
                            <td className="px-3 py-2.5 border-r border-stone-100 uppercase text-xs">{t.employee_designation || "—"}</td>
                            <td className="px-3 py-2.5 border-r border-stone-100 text-right font-semibold tabular-nums">{(t.actual_expense || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2.5 border-r border-stone-100 uppercase">{t.location || "—"}</td>
                            <td className="px-3 py-2.5 border-r border-stone-100 uppercase text-xs">{t.dealer_names || "—"}</td>
                            <td className="px-3 py-2.5"><span className="status-pill capitalize" style={{ background: c.bg, color: c.color }}>{t.status}</span></td>
                            {canApprove && (
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                {t.status === "pending" && (
                                  <div className="flex gap-1">
                                    <button onClick={() => setStatus(t.id, "approved")} data-testid={`tada-approve-${t.id}`} title="Approve"
                                            className="p-1.5 rounded-md text-white" style={{ background: "var(--color-primary)" }}>
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => setStatus(t.id, "rejected")} data-testid={`tada-reject-${t.id}`} title="Reject"
                                            className="p-1.5 rounded-md border border-stone-300 text-stone-600">
                                      <XCircle className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      <tr className="border-t-2 border-stone-400 bg-stone-50 font-bold">
                        <td colSpan="3" className="px-3 py-3 text-right uppercase tracking-wider text-sm">TOTAL</td>
                        <td className="px-3 py-3 text-right tabular-nums text-sm" style={{ color: "var(--color-primary)" }} data-testid="tada-grand-total">
                          {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td colSpan={canApprove ? 4 : 3}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>

      {show && <ClaimModal employees={employees} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

/* ============================ CLAIM MODAL ============================ */
function ClaimModal({ employees, onClose, onCreated }) {
  const [form, setForm] = useState({
    employee_id: employees[0]?.id || "", location: "", purpose: "",
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    km_travelled: 0, hotel: 0, food: 0, local: 0, misc: 0, advance_amount: 0,
  });
  const [ent, setEnt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form.employee_id) return;
    api.get(`/tada/entitlement/${form.employee_id}`).then((r) => setEnt(r.data));
  }, [form.employee_id]);

  // Calculate entitlement breakdown
  const days = Math.max(1, Math.round((new Date(form.to_date) - new Date(form.from_date)) / 86400000) + 1);
  const travel = ent ? Math.round(Number(form.km_travelled) * (ent.per_km_rate || 0)) : 0;
  const da = ent ? (ent.daily_da || 0) * days : 0;
  const hotelEntitled = ent ? Math.min(Number(form.hotel), (ent.hotel_cap_per_day || 0) * days) : Number(form.hotel);
  const foodEntitled = ent ? Math.min(Number(form.food), (ent.food_cap_per_day || 0) * days) : Number(form.food);
  const total = travel + da + hotelEntitled + foodEntitled + Number(form.local) + Number(form.misc);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post("/tada", {
      employee_id: form.employee_id, location: form.location, purpose: form.purpose,
      from_date: form.from_date, to_date: form.to_date,
      advance_amount: Number(form.advance_amount), actual_expense: total,
      dealer_names: form.dealer_names,
      category_breakdown: {
        travel, hotel: hotelEntitled, food: foodEntitled,
        local: Number(form.local), misc: Number(form.misc), da,
      },
    });
    setBusy(false); onCreated();
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="claim-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">New TA / DA Claim</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <label><div className="kpi-label mb-1.5">Employee</div>
          <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.designation}</option>)}
          </select>
        </label>

        {ent && (
          <div className="mt-4 p-4 rounded-md" style={{ background: "var(--color-primary-light)" }}>
            <div className="kpi-label mb-2" style={{ color: "var(--color-primary)" }}>Entitlement · {ent.designation}</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              <Ent icon={ent.vehicle_type === "Car" ? Car : ent.vehicle_type === "Bike" ? Bike : MapPin} label="Vehicle" v={ent.vehicle_type} />
              <Ent label="Per KM" v={`₹${ent.per_km_rate}`} />
              <Ent label="Daily DA" v={formatINR(ent.daily_da)} />
              <Ent icon={Hotel} label="Hotel/day" v={formatINR(ent.hotel_cap_per_day)} />
              <Ent icon={UtensilsCrossed} label="Food/day" v={formatINR(ent.food_cap_per_day)} />
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <In label="Location / Area" value={form.location} onChange={(v) => setForm({ ...form, location: v })} required />
            <In label="Purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} required />
            <In label="From" type="date" value={form.from_date} onChange={(v) => setForm({ ...form, from_date: v })} />
            <In label="To" type="date" value={form.to_date} onChange={(v) => setForm({ ...form, to_date: v })} />
            <In label={`KM Travelled (${ent?.vehicle_type || "—"})`} type="number" value={form.km_travelled} onChange={(v) => setForm({ ...form, km_travelled: v })} />
            <In label="Hotel ₹ (bills)" type="number" value={form.hotel} onChange={(v) => setForm({ ...form, hotel: v })} />
            <In label="Food ₹ (bills)" type="number" value={form.food} onChange={(v) => setForm({ ...form, food: v })} />
            <In label="Local Conveyance ₹" type="number" value={form.local} onChange={(v) => setForm({ ...form, local: v })} />
            <In label="Misc ₹" type="number" value={form.misc} onChange={(v) => setForm({ ...form, misc: v })} />
            <In label="Advance Taken ₹" type="number" value={form.advance_amount} onChange={(v) => setForm({ ...form, advance_amount: v })} />
          </div>

          <In label="Dealer Names (comma-separated)" value={form.dealer_names} onChange={(v) => setForm({ ...form, dealer_names: v })}
              placeholder="e.g. Saloni Krishi Seva Kendra, Maa Agro Sales" />

          <div className="card-flat" style={{ background: "#FDFBF7", padding: 16 }}>
            <div className="kpi-label mb-2">Auto-Computed Settlement</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Row label="Travel (KM × rate)" v={formatINR(travel)} />
              <Row label={`DA × ${days}d`} v={formatINR(da)} />
              <Row label="Hotel (capped)" v={formatINR(hotelEntitled)} />
              <Row label="Food (capped)" v={formatINR(foodEntitled)} />
              <Row label="Local + Misc" v={formatINR(Number(form.local) + Number(form.misc))} />
              <Row bold label="Total Claim" v={formatINR(total)} />
            </div>
            <div className="mt-3 pt-3 border-t border-stone-200 flex justify-between text-sm">
              <span className="font-semibold">Balance vs Advance</span>
              <span className={`font-bold ${(total - Number(form.advance_amount)) >= 0 ? "" : "text-orange-700"}`} style={{ color: total - Number(form.advance_amount) >= 0 ? "var(--color-primary)" : undefined }}>
                {total >= Number(form.advance_amount) ? `Payable ₹${(total - Number(form.advance_amount)).toLocaleString("en-IN")}` : `Recoverable ₹${(Number(form.advance_amount) - total).toLocaleString("en-IN")}`}
              </span>
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="submit-claim-button"
                  className="w-full py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Submitting…" : "Submit Claim"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Ent({ icon: Icon, label, v }) {
  return (
    <div>
      <div className="flex items-center gap-1 kpi-label" style={{ color: "var(--color-primary)" }}>
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-semibold mt-1" style={{ color: "var(--color-primary)" }}>{v}</div>
    </div>
  );
}

function Row({ label, v, bold }) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className={`mt-0.5 ${bold ? "font-bold text-base" : "text-sm"}`}>{v}</div>
    </div>
  );
}

function In({ label, type = "text", value, onChange, required, placeholder }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
             className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
    </label>
  );
}
